import { useServices, useTenantConfig } from 'contexts';
import { useEffect, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useDebounceValue } from './useDebounce';

const MIN_LENGTH_SUGGESTION_TEXT = 4;
export const useSuggestionAddress = ({
  province: defaultProvince,
  district: defaultDistrict,
  ward: defaultWard,
}: {
  province: string;
  district?: string;
  ward: string;
}) => {
  const [suggestInputText, setSuggestInputText] = useState('');
  const [openListSuggestion, setOpenListSuggestion] = useState(false);
  const [province, setProvince] = useState(defaultProvince);
  const [district, setDistrict] = useState(defaultDistrict);
  const [ward, setWard] = useState(defaultWard);
  const [isLoadingGetPlaceDetail, setIsLoadingGetPlaceDetail] = useState(false);

  const { locationServiceV2 } = useServices();
  const { mutate } = useSWRConfig();

  const { epicOmni1589, epicOmni1825SuggestAddress } = useTenantConfig();

  const debounceSuggestInputText = useDebounceValue(suggestInputText, 500);

  const { data: dataSuggestion = [], isValidating } = useSWR(
    !!debounceSuggestInputText &&
      debounceSuggestInputText.length >= MIN_LENGTH_SUGGESTION_TEXT &&
      epicOmni1825SuggestAddress
      ? ['suggestInputText', debounceSuggestInputText]
      : null,
    async () => {
      const res = await locationServiceV2.getAddressSuggestions({
        inputAddress: epicOmni1589
          ? `${debounceSuggestInputText}${ward ? `, ${ward}` : ''}${
              province ? `, ${province}` : ''
            }`
          : `${debounceSuggestInputText}${ward ? `, ${ward}` : ''}${
              district ? `, ${district}` : ''
            }${province ? `, ${province}` : ''}`,
      });

      return res;
    },
    { revalidateOnFocus: false, fallbackData: [], shouldRetryOnError: false },
  );

  const getPlaceDetails = async (placeID: string) => {
    try {
      setIsLoadingGetPlaceDetail(true);
      const placeDetail = await mutate(
        `/location/place-detail/${placeID}`,
        locationServiceV2.getPlaceDetails({
          placeIds: placeID,
        }),
      );

      setIsLoadingGetPlaceDetail(false);
      return placeDetail;
    } catch (error) {
      setIsLoadingGetPlaceDetail(false);
      return null;
    }
  };

  useEffect(() => {
    if (!!defaultProvince) {
      setProvince(defaultProvince);
    }
    if (!!defaultDistrict) {
      setDistrict(defaultDistrict);
    }
    if (!!defaultWard) {
      setWard(defaultWard);
    }
  }, [defaultProvince, defaultDistrict, defaultWard]);

  return {
    setSuggestInputText,
    dataSuggestion: !!dataSuggestion.length ? dataSuggestion : [],
    openListSuggestion,
    setOpenListSuggestion,
    setProvince,
    setDistrict,
    setWard,
    isLoadingSuggest: isValidating || isLoadingGetPlaceDetail,
    getPlaceDetails,
  };
};
